/**
 * PlanBuilder
 * Component for building multiple pricing plans.
 * Each plan has name, monthly/annual/setup prices, description, and features.
 * Supports add, remove, preview, and reorder (up/down).
 */
import React, { useCallback } from 'react';
import PropTypes from 'prop-types';

const EMPTY_PLAN = {
  name: '',
  monthlyPrice: '',
  annualPrice: '',
  setupFee: '',
  description: '',
  features: '',
  isFreeTier: false,
  contactForPricing: false,
};

export default function PlanBuilder({ plans, onChange, error }) {
  const handleAddPlan = useCallback(() => {
    onChange([...plans, { ...EMPTY_PLAN, id: Date.now().toString() }]);
  }, [plans, onChange]);

  const handleRemovePlan = useCallback((index) => {
    onChange(plans.filter((_, i) => i !== index));
  }, [plans, onChange]);

  const handlePlanChange = useCallback((index, field, value) => {
    const updated = plans.map((plan, i) => {
      if (i !== index) return plan;
      const newPlan = { ...plan, [field]: value };
      // If free tier is toggled on, zero out prices
      if (field === 'isFreeTier' && value) {
        newPlan.monthlyPrice = '0';
        newPlan.annualPrice = '0';
        newPlan.setupFee = '0';
        newPlan.contactForPricing = false;
      }
      // If contact for pricing toggled on, clear specific prices
      if (field === 'contactForPricing' && value) {
        newPlan.monthlyPrice = '';
        newPlan.annualPrice = '';
        newPlan.setupFee = '';
        newPlan.isFreeTier = false;
      }
      return newPlan;
    });
    onChange(updated);
  }, [plans, onChange]);

  const handleMoveUp = useCallback((index) => {
    if (index === 0) return;
    const updated = [...plans];
    [updated[index - 1], updated[index]] = [updated[index], updated[index - 1]];
    onChange(updated);
  }, [plans, onChange]);

  const handleMoveDown = useCallback((index) => {
    if (index === plans.length - 1) return;
    const updated = [...plans];
    [updated[index], updated[index + 1]] = [updated[index + 1], updated[index]];
    onChange(updated);
  }, [plans, onChange]);

  return (
    <div>
      {error && (
        <div style={{ fontSize: '12px', color: '#ef4444', marginBottom: '12px' }}>{error}</div>
      )}

      {plans.length === 0 && (
        <div style={{
          padding: '32px',
          textAlign: 'center',
          backgroundColor: '#f8fafc',
          borderRadius: '10px',
          border: '1px dashed #d1d5db',
          marginBottom: '16px',
        }}>
          <p style={{ fontSize: '14px', color: '#64748b', margin: '0 0 12px' }}>
            No plans added yet. Add your first pricing plan below.
          </p>
        </div>
      )}

      {plans.map((plan, index) => (
        <div
          key={plan.id || index}
          style={{
            backgroundColor: '#fff',
            borderRadius: '10px',
            border: '1px solid #e2e8f0',
            padding: '20px',
            marginBottom: '16px',
          }}
        >
          {/* Plan header */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            marginBottom: '16px',
          }}>
            <span style={{ fontSize: '14px', fontWeight: '700', color: '#1e293b' }}>
              Plan {index + 1}{plan.name ? `: ${plan.name}` : ''}
            </span>
            <div style={{ display: 'flex', gap: '6px' }}>
              <button
                type="button"
                onClick={() => handleMoveUp(index)}
                disabled={index === 0}
                style={reorderBtnStyle(index === 0)}
                title="Move up"
              >
                &uarr;
              </button>
              <button
                type="button"
                onClick={() => handleMoveDown(index)}
                disabled={index === plans.length - 1}
                style={reorderBtnStyle(index === plans.length - 1)}
                title="Move down"
              >
                &darr;
              </button>
              <button
                type="button"
                onClick={() => handleRemovePlan(index)}
                style={{
                  padding: '4px 10px',
                  fontSize: '12px',
                  fontWeight: '600',
                  color: '#ef4444',
                  backgroundColor: '#fef2f2',
                  border: '1px solid #fecaca',
                  borderRadius: '6px',
                  cursor: 'pointer',
                }}
                title="Remove plan"
              >
                Remove
              </button>
            </div>
          </div>

          {/* Plan name */}
          <div style={{ marginBottom: '12px' }}>
            <label style={labelStyle}>Plan Name *</label>
            <input
              type="text"
              value={plan.name}
              onChange={(e) => handlePlanChange(index, 'name', e.target.value)}
              style={inputStyle}
              placeholder="e.g., Basic, Professional, Enterprise"
            />
          </div>

          {/* Toggles */}
          <div style={{ display: 'flex', gap: '16px', marginBottom: '12px', flexWrap: 'wrap' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', color: '#475569', cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={plan.isFreeTier}
                onChange={(e) => handlePlanChange(index, 'isFreeTier', e.target.checked)}
                style={{ width: '16px', height: '16px' }}
              />
              Free Tier
            </label>
            <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', color: '#475569', cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={plan.contactForPricing}
                onChange={(e) => handlePlanChange(index, 'contactForPricing', e.target.checked)}
                style={{ width: '16px', height: '16px' }}
              />
              Contact for Pricing
            </label>
          </div>

          {/* Prices - only shown if not "contact for pricing" */}
          {!plan.contactForPricing && (
            <div style={{ display: 'flex', gap: '12px', marginBottom: '12px', flexWrap: 'wrap' }}>
              <div style={{ flex: '1 1 140px' }}>
                <label style={labelStyle}>Monthly Price (CAD)</label>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={plan.monthlyPrice}
                  onChange={(e) => handlePlanChange(index, 'monthlyPrice', e.target.value)}
                  style={inputStyle}
                  placeholder="0.00"
                  disabled={plan.isFreeTier}
                />
              </div>
              <div style={{ flex: '1 1 140px' }}>
                <label style={labelStyle}>Annual Price (CAD)</label>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={plan.annualPrice}
                  onChange={(e) => handlePlanChange(index, 'annualPrice', e.target.value)}
                  style={inputStyle}
                  placeholder="0.00"
                  disabled={plan.isFreeTier}
                />
              </div>
              <div style={{ flex: '1 1 140px' }}>
                <label style={labelStyle}>Setup Fee (CAD)</label>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={plan.setupFee}
                  onChange={(e) => handlePlanChange(index, 'setupFee', e.target.value)}
                  style={inputStyle}
                  placeholder="0.00"
                  disabled={plan.isFreeTier}
                />
              </div>
            </div>
          )}

          {/* Description */}
          <div style={{ marginBottom: '12px' }}>
            <label style={labelStyle}>Plan Description</label>
            <textarea
              value={plan.description}
              onChange={(e) => handlePlanChange(index, 'description', e.target.value)}
              rows={2}
              style={{ ...inputStyle, resize: 'vertical', fontFamily: 'inherit' }}
              placeholder="Brief description of what this plan includes..."
            />
          </div>

          {/* Features */}
          <div style={{ marginBottom: '12px' }}>
            <label style={labelStyle}>Features (comma-separated)</label>
            <textarea
              value={plan.features}
              onChange={(e) => handlePlanChange(index, 'features', e.target.value)}
              rows={2}
              style={{ ...inputStyle, resize: 'vertical', fontFamily: 'inherit' }}
              placeholder="Feature 1, Feature 2, Feature 3..."
            />
          </div>

          {/* Preview card */}
          {plan.name && (
            <div style={{
              backgroundColor: '#f8fafc',
              borderRadius: '8px',
              border: '1px solid #e2e8f0',
              padding: '16px',
            }}>
              <div style={{ fontSize: '11px', fontWeight: '700', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '8px' }}>
                Preview
              </div>
              <div style={{ fontSize: '16px', fontWeight: '700', color: '#1e293b', marginBottom: '4px' }}>
                {plan.name}
              </div>
              {plan.contactForPricing ? (
                <div style={{ fontSize: '14px', color: '#3b82f6', fontWeight: '600', marginBottom: '8px' }}>
                  Contact for Pricing
                </div>
              ) : (
                <div style={{ fontSize: '14px', color: '#1e293b', marginBottom: '8px' }}>
                  {plan.isFreeTier ? (
                    <span style={{ fontWeight: '700', color: '#22c55e' }}>Free</span>
                  ) : (
                    <>
                      {plan.monthlyPrice && (
                        <span style={{ fontWeight: '700' }}>${plan.monthlyPrice}/mo</span>
                      )}
                      {plan.annualPrice && (
                        <span style={{ color: '#64748b', marginLeft: '8px' }}>
                          (${plan.annualPrice}/yr)
                        </span>
                      )}
                      {plan.setupFee && plan.setupFee !== '0' && (
                        <span style={{ color: '#94a3b8', marginLeft: '8px', fontSize: '12px' }}>
                          + ${plan.setupFee} setup
                        </span>
                      )}
                    </>
                  )}
                </div>
              )}
              {plan.description && (
                <div style={{ fontSize: '13px', color: '#64748b', marginBottom: '8px' }}>
                  {plan.description}
                </div>
              )}
              {plan.features && (
                <ul style={{ margin: 0, paddingLeft: '16px' }}>
                  {plan.features.split(',').filter(Boolean).map((f, i) => (
                    <li key={i} style={{ fontSize: '12px', color: '#475569', marginBottom: '2px' }}>
                      {f.trim()}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}
        </div>
      ))}

      {/* Add plan button */}
      <button
        type="button"
        onClick={handleAddPlan}
        style={{
          width: '100%',
          padding: '12px',
          fontSize: '14px',
          fontWeight: '600',
          color: '#3b82f6',
          backgroundColor: '#f0f9ff',
          border: '2px dashed #93c5fd',
          borderRadius: '10px',
          cursor: 'pointer',
        }}
      >
        + Add Pricing Plan
      </button>
    </div>
  );
}

PlanBuilder.propTypes = {
  plans: PropTypes.arrayOf(PropTypes.shape({
    name: PropTypes.string,
    monthlyPrice: PropTypes.string,
    annualPrice: PropTypes.string,
    setupFee: PropTypes.string,
    description: PropTypes.string,
    features: PropTypes.string,
    isFreeTier: PropTypes.bool,
    contactForPricing: PropTypes.bool,
  })).isRequired,
  onChange: PropTypes.func.isRequired,
  error: PropTypes.string,
};

function reorderBtnStyle(disabled) {
  return {
    padding: '4px 8px',
    fontSize: '14px',
    fontWeight: '700',
    color: disabled ? '#cbd5e1' : '#475569',
    backgroundColor: disabled ? '#f8fafc' : '#f1f5f9',
    border: '1px solid #e2e8f0',
    borderRadius: '6px',
    cursor: disabled ? 'not-allowed' : 'pointer',
    lineHeight: 1,
  };
}

const labelStyle = {
  display: 'block',
  fontSize: '13px',
  fontWeight: '600',
  color: '#374151',
  marginBottom: '4px',
};

const inputStyle = {
  width: '100%',
  padding: '10px 12px',
  fontSize: '14px',
  borderRadius: '8px',
  border: '1px solid #d1d5db',
  backgroundColor: '#fff',
  color: '#1e293b',
  outline: 'none',
  boxSizing: 'border-box',
};
